import React, { useState, useRef } from 'react';
import { ImageWithAlt } from '@/types/ImageWithAltInterface';
import { Image } from '@/components';

interface VideoProps {
  videoUrl: string;
  thumbnail?: ImageWithAlt;
  title: string; // Add a required title prop for better accessibility
}

const Video: React.FC<VideoProps> = ({ videoUrl, thumbnail, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLIFrameElement>(null);

  if (!videoUrl) {
    return null;
  }

  // Function to extract YouTube or Vimeo video ID from the URL
  const getVideoId = (videoUrl: string): { platform: 'youtube' | 'vimeo'; id: string } | null => {
    // YouTube regex
    const youtubeRegExp =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    // Vimeo regex
    const vimeoRegExp = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/;

    const youtubeMatch = videoUrl.match(youtubeRegExp);
    if (youtubeMatch) {
      return { platform: 'youtube', id: youtubeMatch[1] };
    }

    const vimeoMatch = videoUrl.match(vimeoRegExp);
    if (vimeoMatch) {
      return { platform: 'vimeo', id: vimeoMatch[1] };
    }

    return null;
  };

  const videoInfo = getVideoId(videoUrl);

  if (!videoInfo) {
    return <p>Invalid YouTube or Vimeo URL</p>;
  }

  const { platform, id } = videoInfo;

  const embedUrl =
    platform === 'youtube'
      ? `https://www.youtube.com/embed/${id}?autoplay=1`
      : `https://player.vimeo.com/video/${id}?autoplay=1`;

  const defaultThumbnail =
    platform === 'youtube' ? `https://i.ytimg.com/vi/${id}/maxresdefault.jpg` : null;

  const handlePlay = () => {
    setIsPlaying(true);
    // Focus on the iframe when the video starts playing
    if (videoRef.current) {
      videoRef.current.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handlePlay();
    }
  };

  const ThumbnailImage = () => {
    if (thumbnail) {
      return (
        <Image
          image={thumbnail.image}
          alt={thumbnail.alt}
          className='h-full w-full object-cover transition-transform duration-300 hover:scale-105'
          width={1125}
        />
      );
    } else if (defaultThumbnail) {
      return (
        <img
          src={defaultThumbnail}
          alt={`Thumbnail for ${title}`}
          className='h-full w-full object-cover transition-transform duration-300 hover:scale-105'
        />
      );
    }
    return null;
  };

  return (
    <div className='relative h-0 overflow-hidden bg-black pb-[56.25%]'>
      {!isPlaying ? (
        <div
          className='absolute left-0 top-0 h-full w-full cursor-pointer overflow-hidden'
          onClick={handlePlay}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role='button'
          aria-label={`Play ${title}`}
        >
          <ThumbnailImage />
          <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
            <svg
              width='64'
              height='63'
              viewBox='0 0 64 63'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden='true'
            >
              <circle
                cx='32'
                cy='31.5'
                r='31.5'
                fill='#FFD700'
              />
              <path
                d='M42.125 29.5514C43.625 30.4175 43.625 32.5825 42.125 33.4486L28.625 41.2428C27.125 42.1088 25.25 41.0263 25.25 39.2942L25.25 23.7058C25.25 21.9737 27.125 20.8912 28.625 21.7572L42.125 29.5514Z'
                fill='black'
              />
            </svg>
          </div>
        </div>
      ) : (
        <iframe
          ref={videoRef}
          className='absolute left-0 top-0 h-full w-full'
          src={embedUrl}
          title={title}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
        ></iframe>
      )}
    </div>
  );
};

export default Video;
